import { useMemo, useState } from "react";
import { sendSmartAssistantMessage } from "../../api/aiAssistant";

export const DEFAULT_ELLYANNA_AI_CONTEXT = {
  product_area: "unknown",
  user_type: "owner",
  province: "Ontario",
  language: "English",
};

export const ELLYANNA_AI_MESSAGE_MAX_LENGTH = 2000;
export const ELLYANNA_AI_UNAVAILABLE_MESSAGE =
  "The Ellyanna AI assistant is temporarily unavailable. Please try again later.";

const QUICK_QUESTIONS = [
  "How do I set up staff?",
  "How do I add services and categories?",
  "How do I set up clients?",
  "How do I configure online booking?",
  "How do I set up Ellyanna Boothshare workspaces?",
  "How do I use Ellyanna Accounting checklists?",
  "What is the difference between gel and shellac?",
];

function getRouteLabel(routeUsed) {
  if (routeUsed === "manual_assistant") return "Answered from: Ellyanna Manual";
  if (routeUsed === "salon_assistant") return "Answered from: Salon Guidance";
  return "";
}

export default function EllyannaAiAssistant({ context }) {
  const assistantContext = useMemo(
    () => ({
      ...DEFAULT_ELLYANNA_AI_CONTEXT,
      ...(context || {}),
    }),
    [context]
  );
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState("");
  const [routeUsed, setRouteUsed] = useState("");
  const [mode, setMode] = useState("");
  const [sources, setSources] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const charactersRemaining = ELLYANNA_AI_MESSAGE_MAX_LENGTH - message.length;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedMessage = message.trim();

    if (!trimmedMessage) {
      setError("Please type a question before sending.");
      setReply("");
      setRouteUsed("");
      setMode("");
      setSources([]);
      return;
    }

    if (trimmedMessage.length > ELLYANNA_AI_MESSAGE_MAX_LENGTH) {
      setError(`Please keep your question under ${ELLYANNA_AI_MESSAGE_MAX_LENGTH} characters.`);
      setReply("");
      setRouteUsed("");
      setMode("");
      setSources([]);
      return;
    }

    setLoading(true);
    setError("");
    setReply("");
    setRouteUsed("");
    setMode("");
    setSources([]);

    try {
      const data = await sendSmartAssistantMessage({
        message: trimmedMessage,
        context: assistantContext,
      });
      setReply(data?.reply || "The assistant did not return a reply.");
      setRouteUsed(data?.route_used || "");
      setMode(data?.mode || "");
      setSources(Array.isArray(data?.sources) ? data.sources : []);
    } catch (err) {
      setError(ELLYANNA_AI_UNAVAILABLE_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  const routeLabel = getRouteLabel(routeUsed);
  const isReadOnly = mode === "manual_read_only" || mode === "read_only";

  return (
    <section className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-semibold text-slate-900">Ellyanna AI Assistant</h1>
            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">
              Read-only assistant
            </span>
          </div>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Ask for salon guidance or help using Ellyanna.
          </p>
          <p className="mt-1 max-w-2xl text-xs text-slate-500">
            The assistant can explain and guide, but cannot create, cancel, reschedule, update,
            delete, or modify records.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-5">
          <div className="flex flex-wrap gap-2" aria-label="Example questions">
            {QUICK_QUESTIONS.map((question) => (
              <button
                key={question}
                type="button"
                onClick={() => {
                  setMessage(question);
                  if (error) setError("");
                }}
                className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-white"
                disabled={loading}
              >
                {question}
              </button>
            ))}
          </div>

          <div className="space-y-2">
            <label htmlFor="ellyanna-ai-message" className="text-sm font-semibold text-slate-700">
              Ask a question
            </label>
            <textarea
              id="ellyanna-ai-message"
              value={message}
              onChange={(event) => {
                setMessage(event.target.value);
                if (error) setError("");
              }}
              rows={5}
              className="w-full rounded-lg border border-slate-300 px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              placeholder="Ask about booking steps, salon policies, services, or what to do next."
              disabled={loading}
            />
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
              <span>Answers are read-only and should be reviewed before making changes.</span>
              <span className={charactersRemaining < 0 ? "font-semibold text-red-600" : ""}>
                {Math.max(charactersRemaining, 0)} characters left
              </span>
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex min-h-11 items-center justify-center rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
            >
              {loading ? "Sending..." : "Send"}
            </button>
          </div>
        </form>

        <div className="border-t border-slate-200 px-5 py-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Reply</h2>
          {(routeLabel || isReadOnly) && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              {routeLabel && (
                <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 font-semibold text-blue-700">
                  {routeLabel}
                </span>
              )}
              {isReadOnly && (
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 font-semibold text-emerald-700">
                  Read-only answer
                </span>
              )}
            </div>
          )}
          <div className="mt-3 min-h-28 whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 px-4 py-4 text-sm leading-6 text-slate-800">
            {reply || "The assistant reply will appear here."}
          </div>
          {sources.length > 0 && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-white px-4 py-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Sources
              </h3>
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                {sources.map((source, index) => (
                  <li key={`${source.file}-${source.title}-${index}`}>
                    <span className="font-medium">{source.title || "Manual source"}</span>
                    {source.file ? <span className="text-slate-500"> - {source.file}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
